import { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import {
	useDeleteCompanyBenefitTax,
	useGetCompanyBenefitTax,
} from "../../../../../../services/company-benefit-tax.service";
import ActionPopover from "../../../../../../components/popovers/actionPopover";
import DeleteModal from "../../../../../../components/modals/DeleteModal";
import { BenefitTaxDto } from "../../../../../../models/benefit-tax.dto";
import TableFilter from "../../../../../../components/table/filter/TableFilter";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../../helpers/useManageTable";
import { benefitsTaxRuleColumns } from "../../../../../admin/system_configurations/views/tax_rules/tanstack-columns";
import TanStackEmployeeTable from "../../../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../../../models/table-configuration.dto";
import CompanyBenefitTaxRuleModalForm from "../modals/BenefitTaxRuleModalForm";

interface Props {
	companyId: number;
}

const CompanyBenefitsTaxRuleView = ({ companyId }: Props) => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);

	const { tableAPIRef, tableData, isLoading, pageCount } =
		useManageTableWithSearchParams<BenefitTaxDto>({
			fetchHook: (params) => useGetCompanyBenefitTax(companyId, params),
		});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteBenefitTax } = useDeleteCompanyBenefitTax();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);
		deleteBenefitTax(
			{ companyId, id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	return (
		<>
			<div className="flex justify-between items-center mt-7 mb-8">
				<TableFilter
					fields={[
						{
							title: "",
							fieldName: "",
							fieldType: "SORT",
							options: [
								{ title: "Date Created", fieldName: "createdAt" },
								{ title: "Date Modified", fieldName: "modifiedAt" },
							],
						},
						{
							title: "Final Tax",
							fieldName: "isFinalTax",
							fieldType: "YES_NO",
						},
					]}
				/>
				<div
					className="flex items-center text-primary-blue text-sm cursor-pointer"
					onClick={handleOpenModal}
				>
					<AiOutlinePlus className="mr-1 text-sm" />
					Add New
				</div>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.BENEFITS_TAX_RULE}
				data={tableData}
				columns={benefitsTaxRuleColumns}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<CompanyBenefitTaxRuleModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Benefit Rule" : "Add New Benefit Rule"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					companyId={companyId}
					reloadTableData={reloadTableData}
				/>
			)}
		</>
	);
};

export default CompanyBenefitsTaxRuleView;
